/**
 * Utility functions for map API queries
 */
import type { Map as MapboxMap } from "mapbox-gl";
import type { MapBoundingBox } from "./types";
import type { GeoJsonFeature } from "./types";

/**
 * Converts Mapbox viewport bounds to bounding box string format: "minLon,minLat,maxLon,maxLat"
 */
export const getBoundingBoxFromMap = (map: MapboxMap): string | undefined => {
    const bounds = map.getBounds();
    if (!bounds) return undefined;
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();

    // Add small padding to ensure we get features near edges
    const padding = 0.001;

    return `${sw.lng - padding},${sw.lat - padding},${ne.lng + padding},${ne.lat + padding}`;
};

/**
 * Parses bounding box string to MapBoundingBox object
 */
export const parseBoundingBox = (
    bbox: string
): MapBoundingBox | undefined => {
    const parts = bbox.split(",").map((p) => parseFloat(p.trim()));
    if (parts.length !== 4 || parts.some((p) => isNaN(p))) {
        return undefined;
    }

    const [minLon, minLat, maxLon, maxLat] = parts;

    // Validate bounds
    if (
        minLon >= maxLon ||
        minLat >= maxLat ||
        minLon < -180 ||
        maxLon > 180 ||
        minLat < -90 ||
        maxLat > 90
    ) {
        return undefined;
    }

    return { minLongitude: minLon, minLatitude: minLat, maxLongitude: maxLon, maxLatitude: maxLat };
};

/**
 * Extracts bounding box string from a GeoJSON feature (Polygon or MultiPolygon).
 * Returns "minLon,minLat,maxLon,maxLat" or undefined if geometry is invalid.
 */
export const getBoundingBoxFromFeature = (
    feature: GeoJsonFeature<unknown>
): string | undefined => {
    const geom = feature.geometry;
    if (!geom || !geom.coordinates) return undefined;
    let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;

    const processCoords = (coords: number[] | number[][] | number[][][]) => {
        if (typeof coords[0] === "number") {
            const [lon, lat] = coords as number[];
            minLon = Math.min(minLon, lon);
            minLat = Math.min(minLat, lat);
            maxLon = Math.max(maxLon, lon);
            maxLat = Math.max(maxLat, lat);
        } else {
            (coords as (number[] | number[][] | number[][][])[]).forEach(processCoords);
        }
    };

    if (geom.type === "Polygon") {
        (geom.coordinates as number[][][]).forEach((ring) => ring.forEach(processCoords));
    } else if (geom.type === "MultiPolygon") {
        (geom.coordinates as number[][][][]).forEach((poly) =>
            poly.forEach((ring) => ring.forEach(processCoords))
        );
    } else {
        return undefined;
    }

    if (minLon === Infinity || minLat === Infinity) return undefined;
    const padding = 0.005;
    return `${minLon - padding},${minLat - padding},${maxLon + padding},${maxLat + padding}`;
};

/**
 * Calculates bounding box width and height in degrees
 */
export const getBoundingBoxDimensions = (
    bbox: MapBoundingBox
): { width: number; height: number } => {
    return {
        width: bbox.maxLongitude - bbox.minLongitude,
        height: bbox.maxLatitude - bbox.minLatitude
    };
};

/**
 * Debounce function for viewport updates
 */
export const debounce = <T extends (...args: unknown[]) => void>(
    func: T,
    wait: number
): ((...args: Parameters<T>) => void) => {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    return (...args: Parameters<T>) => {
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(() => {
            func(...args);
        }, wait);
    };
};